import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProductType } from "../models/ProductType";
import {
	removeProductType,
	upsertProductType,
	type UpsertProductTypeResult,
} from "../repositories/ProductTypesRepository";
import { PRODUCT_TYPES_QUERY_KEYS } from "./useProductTypes";

interface MutationOptions {
	onSuccess?: (data?: ProductType) => void;
	onError?: (error: Error) => void;
}

interface UpsertMutationOptions {
	onError?: (error: Error) => void;
}

export const useUpsertProductType = ({
	onError,
}: UpsertMutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async ({
			productType,
			force,
		}: {
			productType: ProductType;
			force?: boolean;
		}): Promise<UpsertProductTypeResult> => {
			return await upsertProductType(productType, force ?? false);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_TYPES_QUERY_KEYS.all,
			});
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertProductTypeAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteProductType = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeProductType(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_TYPES_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteProductType: mutation.mutate,
		deleteProductTypeAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
